import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { DateInput } from '@/components/DateInput';
import { GeneralErrorIndicator } from '@/components/GeneralErrorIndicator';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { links } from '@/lib/constants';
import { dateOfBirthSchema } from '@/lib/validation-schemas';
import { recipientOptions } from '@/types/patient-info.type';

import { ContactSupportLink } from './ContactSupportLink';

const formId = 'order-lookup-identity-form';

const formSchema = z.object({
  recipient: z.enum(
    recipientOptions.map((option) => option.value),
    { message: 'Please make a selection' }
  ),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: dateOfBirthSchema,
});

type OrderLookupIdentityFormData = z.infer<typeof formSchema>;

interface OrderLookupIdentityStepProps {
  defaultValues?: Partial<OrderLookupIdentityFormData>;
  onBack: () => void;
  onSubmit: (data: OrderLookupIdentityFormData) => void;
  isPending: boolean;
  error?: string;
}

export function OrderLookupIdentityStep({
  defaultValues,
  onBack,
  onSubmit,
  isPending,
  error,
}: OrderLookupIdentityStepProps) {
  const form = useForm<OrderLookupIdentityFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      recipient: defaultValues?.recipient,
      firstName: defaultValues?.firstName ?? '',
      lastName: defaultValues?.lastName ?? '',
      dateOfBirth: defaultValues?.dateOfBirth ?? '',
    },
  });

  return (
    <section>
      <NavigationBar onBack={onBack} backLabel="Categories" />
      <Card>
        <CardHeader>
          <CardTitle>Let’s get your information to check the order</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8 sm:space-y-12">
          <form
            id={formId}
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8 sm:space-y-12"
          >
            <FieldGroup>
              <Controller
                name="recipient"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FieldSet>
                    <FieldLegend>Who are you checking the order for?</FieldLegend>
                    <RadioGroup
                      name={field.name}
                      value={field.value ?? ''}
                      onValueChange={field.onChange}
                      aria-invalid={fieldState.invalid}
                    >
                      {recipientOptions.map((option) => (
                        <Field
                          orientation="horizontal"
                          data-invalid={fieldState.invalid}
                          key={option.value}
                          className="flex w-auto items-center gap-2"
                        >
                          <RadioGroupItem
                            value={option.value}
                            id={`${formId}-recipient-${option.value}`}
                            aria-invalid={fieldState.invalid}
                          />
                          <FieldLabel htmlFor={`${formId}-recipient-${option.value}`}>
                            {option.label}
                          </FieldLabel>
                        </Field>
                      ))}
                    </RadioGroup>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </FieldSet>
                )}
              />

              <Controller
                name="firstName"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={`${formId}-firstName`}>Patient first name</FieldLabel>
                    <Input
                      {...field}
                      id={`${formId}-firstName`}
                      aria-invalid={fieldState.invalid}
                    />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />

              <Controller
                name="lastName"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={`${formId}-lastName`}>Patient last name</FieldLabel>
                    <Input {...field} id={`${formId}-lastName`} aria-invalid={fieldState.invalid} />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />

              <Controller
                name="dateOfBirth"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={`${formId}-dateOfBirth`}>Patient date of birth</FieldLabel>
                    <DateInput
                      {...field}
                      id={`${formId}-dateOfBirth`}
                      aria-invalid={fieldState.invalid}
                    />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
            </FieldGroup>

            {error && <GeneralErrorIndicator>{error}</GeneralErrorIndicator>}

            <p className="text-xs text-charcoal-500">
              By continuing, you agree to be contacted by Conduit Health and have read and agree to
              these{' '}
              <a
                href={links.conduit_terms_of_service_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs"
              >
                terms of service
              </a>{' '}
              and{' '}
              <a
                href={links.conduit_privacy_policy_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs"
              >
                privacy policy
              </a>{' '}
              and{' '}
              <a
                href={links.conduit_hipaa_notice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs"
              >
                HIPAA notice of privacy practices
              </a>
              .
            </p>

            <Button
              type="submit"
              disabled={isPending}
              className="w-full sm:w-auto sm:min-w-[186px]"
            >
              {isPending ? 'Checking...' : 'Continue'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <ContactSupportLink className="mt-6" />
    </section>
  );
}

export type { OrderLookupIdentityFormData };
